/**
 * 输入期竖屏展示（2026-10-12 起替代 MobileKeyboardSheet 竖屏代理输入条）。
 *
 * 虚拟横屏（手机物理竖屏 + #app-root 旋转 90°）下，系统键盘永远按物理方向
 * 从底部弹出——用户视角里从侧边滑出、按键横躺；旧代理条只救了输入框的正立，
 * 救不了按键。这里换思路：文本输入框聚焦时把整个展示切到真竖屏
 * （createApp 的 mobileInputPortrait 覆盖：#app-root 摘旋转、canvas 按竖屏
 * 尺寸重排），键盘与 UI 同向匹配，用户把手机转正即是正立画面；输入结束
 * （失焦 / 键盘收起 / 面板关闭）后切回虚拟横屏。
 *
 * 要点：
 * - 真实输入框全程保持焦点（不像代理条那样 blur 转交）：类切换与 canvas
 *   重排都不掉焦点、不掉键盘；同面板多字段跳转只换引用不重新布局。
 * - iOS「藏键盘」按钮/返回手势收键盘但保留焦点：visualViewport 高度先明显
 *   压低再恢复即判定键盘已收，主动 blur 走收尾路径切回横屏。
 * - 输入中途视口真转横屏（无方向锁用户物理旋转）：createApp 解除覆盖并摘
 *   mobile-input-portrait 类，下面的哨兵发现类没了即静默收尾（此时键盘已按
 *   真横屏方向弹出，无需干预）。
 * - CSS 侧（styles.css 的 #app-root.mobile-input-portrait）会隐藏横屏 HUD
 *   浮件并强制输入框 ≥16px——iOS 聚焦小于 16px 的输入框会整页缩放。
 */

import { setMobileInputPortrait } from '../app/createApp';

/** 会承接系统键盘的文本类输入框（滑杆/复选框等不切竖屏）。 */
const TEXT_INPUT_SELECTOR = [
    'input[type="text"]', 'input[type="password"]', 'input[type="email"]',
    'input[type="number"]', 'input[type="tel"]', 'input[type="search"]',
    'input[type="url"]', 'input:not([type])', 'textarea',
].join(',');

/** 失焦去抖（ms）：同面板字段间跳转（focusout→focusin 相邻毫秒级）不掉回
 *  横屏，避免每个字段来回翻转一次画面。 */
const DEACTIVATE_DEBOUNCE_MS = 350;
/** visualViewport 键盘判定阈值（px）：压低超过它才算键盘弹出，回升超过它
 *  才算键盘收起——躲开 iOS 工具栏伸缩之类的小幅波动。 */
const KEYBOARD_VV_THRESHOLD = 120;

let activeInput: HTMLInputElement | HTMLTextAreaElement | null = null;
let deactivateTimer = 0;
let watchdogTimer = 0;
let vvCleanup: (() => void) | null = null;
/** 激活时的可见高度（键盘未弹）。 */
let vvStart = 0;
/** 键盘弹出后见过的最低可见高度；0 = 尚未见键盘明显压低。 */
let vvFloor = 0;

function isTextInput(el: EventTarget | null): el is HTMLInputElement | HTMLTextAreaElement {
    return el instanceof HTMLElement && el.matches(TEXT_INPUT_SELECTOR);
}

function cancelDeactivate(): void {
    if (deactivateTimer) { window.clearTimeout(deactivateTimer); deactivateTimer = 0; }
}

function deactivate(): void {
    const input = activeInput;
    activeInput = null;
    vvStart = 0;
    vvFloor = 0;
    cancelDeactivate();
    if (watchdogTimer) { window.clearInterval(watchdogTimer); watchdogTimer = 0; }
    vvCleanup?.();
    vvCleanup = null;
    // 兜底：键盘已收却仍持焦（如哨兵路径面板先没了），别让无键盘的焦点
    // 钉住画面。常规路径这里已 blur 过，条件不成立即无操作。
    if (input?.isConnected && document.activeElement === input) input.blur();
    setMobileInputPortrait(false);
}

/** 键盘收起判定：vv 先明显压低（键盘弹出）再明显回升（键盘收起）而焦点
 *  仍在输入框——iOS 藏键盘按钮/返回手势的形态。主动 blur 触发收尾。 */
function onVvResize(): void {
    if (!activeInput) return;
    const h = Math.round(window.visualViewport?.height ?? 0);
    if (h <= 0) return;
    if (vvFloor === 0) {
        if (h < vvStart - KEYBOARD_VV_THRESHOLD) vvFloor = h;
        return;
    }
    if (h > vvFloor + KEYBOARD_VV_THRESHOLD && document.activeElement === activeInput) {
        activeInput.blur();
    }
}

function arm(input: HTMLInputElement | HTMLTextAreaElement): void {
    activeInput = input;
    cancelDeactivate();
    if (!watchdogTimer) {
        watchdogTimer = window.setInterval(() => {
            if (!activeInput) return;
            // 面板被关（输入框脱离 DOM）或 createApp 已解除覆盖（类被摘）：
            // 都该收尾切回横屏。
            if (!activeInput.isConnected
                || !document.getElementById('app-root')?.classList.contains('mobile-input-portrait')) {
                deactivate();
            }
        }, 400);
    }
    if (!vvCleanup) {
        const vv = window.visualViewport;
        if (vv) {
            vv.addEventListener('resize', onVvResize);
            vvCleanup = () => vv.removeEventListener('resize', onVvResize);
        }
    }
}

export function installInputPortraitMode(): void {
    const appRoot = document.getElementById('app-root');
    if (!appRoot) return;
    document.addEventListener('focusin', (e) => {
        if (!isTextInput(e.target) || !appRoot.contains(e.target)) return;
        if (activeInput) {
            // 已在输入期竖屏：字段间跳转只换引用（键盘不掉、布局不动）。
            if (e.target !== activeInput) arm(e.target);
            return;
        }
        if (appRoot.classList.contains('mobile-input-portrait')) {
            // 防御：收尾竞态期间类还没摘又有新焦点——认领它继续输入期，
            // 否则会出现「竖屏画面 + 无控制器状态」卡死。
            vvStart = Math.round(window.visualViewport?.height ?? window.innerHeight);
            arm(e.target);
            return;
        }
        if (!appRoot.classList.contains('mobile-virtual-landscape')) return; // 桌面/原生横屏：键盘天然从场景底部弹出
        vvStart = Math.round(window.visualViewport?.height ?? window.innerHeight);
        vvFloor = 0;
        arm(e.target);
        setMobileInputPortrait(true);
    }, true);
    document.addEventListener('focusout', () => {
        if (!activeInput) return;
        // 立刻重新 schedule：若焦点马上落到下一个文本框，focusin 会取消它。
        cancelDeactivate();
        deactivateTimer = window.setTimeout(() => {
            deactivateTimer = 0;
            if (!activeInput) return;
            const el = document.activeElement;
            // 焦点仍在（或又落到别的输入类控件，如滑杆）：保守维持竖屏，
            // 等真正的失焦/键盘收起/面板关闭信号。
            if (el instanceof HTMLElement
                && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
            deactivate();
        }, DEACTIVATE_DEBOUNCE_MS);
    }, true);
}
